import { defineStore } from "pinia";
import axios from "axios";
import { PREFIX } from "./auth_store";

export const useOrganizationStore = defineStore("OrganizationStore", {
  id: "organization",
  state: () => ({
    organizations: [],
    currentOrganization: null,
    isLoading: false,
    error: null
  }),
  
  getters: {
    getCurrentOrganization: (state) => {
      // Try to get from state first
      if (state.currentOrganization) {
        return state.currentOrganization;
      }
      
      // Try to get from localStorage
      const storedOrg = localStorage.getItem(`${PREFIX}current_organization`);
      if (storedOrg) {
        try {
          return JSON.parse(storedOrg);
        } catch (e) {
          console.error("Error parsing stored organization:", e);
        }
      }
      
      return null;
    },
    
    getOrganizations: (state) => state.organizations,
    
    getIsLoading: (state) => state.isLoading,
    
    getError: (state) => state.error,
    
    // Check if user is an admin of the current organization
    isOrgAdmin: (state) => {
      return state.currentOrganization?.is_admin || false;
    }
  },
  
  actions: {
    // Fetch all organizations the user has access to
    async fetchOrganizations() {
      this.isLoading = true;
      this.error = null;
      
      try {
        console.log("Using mock organizations data for testing");
        
        // Create mock organizations data
        const mockOrganizations = [
          {
            id: "mock-org-1",
            name: "Test Organization",
            description: "Organization for testing purposes",
            is_admin: true
          }
        ];
        
        console.log("Organizations response:", mockOrganizations);
        
        this.organizations = mockOrganizations;
        this.isLoading = false;
        return this.organizations;
      } catch (error) {
        this.error = "Failed to fetch organizations";
        console.error("Error fetching organizations:", error);
        this.isLoading = false;
        throw error;
      }
    },
    
    // Create a new organization
    async createOrganization(organizationData) {
      this.isLoading = true;
      this.error = null;
      
      try {
        const rootBase = process.env.VUE_APP_ENV_ROOT_BASE;
        const response = await axios.post(`${rootBase}organizations`, { organization: organizationData });
        
        // Fix: Initialize organizations array if it doesn't exist\n        if (!this.organizations || !Array.isArray(this.organizations)) {\n          this.organizations = [];\n        }\n        \n        // Add the new organization to the list
        this.organizations.push(response.data);
        
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to create organization";
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    
    // Set the current organization
    setCurrentOrganization(organization) {
      this.currentOrganization = organization;
      
      // Store in localStorage for persistence
      localStorage.setItem(`${PREFIX}current_organization`, JSON.stringify(organization));
    },
    
    // Invite a user to the organization
    async inviteUser(email, role) {
      this.isLoading = true;
      this.error = null;
      
      try {
        const rootBase = process.env.VUE_APP_ENV_ROOT_BASE;
        const response = await axios.post(`${rootBase}organizations/${this.currentOrganization.id}/invite`, {
          email,
          role
        });
        
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to invite user";
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    
    // Get all users in the current organization
    async getOrganizationUsers() {
      this.isLoading = true;
      this.error = null;
      
      try {
        const rootBase = process.env.VUE_APP_ENV_ROOT_BASE;
        const response = await axios.get(`${rootBase}organizations/${this.currentOrganization.id}/users`);
        
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch organization users";
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    
    // Update user role in the organization
    async updateUserRole(userId, role) {
      this.isLoading = true;
      this.error = null;
      
      try {
        const rootBase = process.env.VUE_APP_ENV_ROOT_BASE;
        const response = await axios.put(`${rootBase}organizations/${this.currentOrganization.id}/users/${userId}`, {
          role
        });
        
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to update user role";
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    
    // Remove a user from the organization
    async removeUser(userId) {
      this.isLoading = true;
      this.error = null;
      
      try {
        const rootBase = process.env.VUE_APP_ENV_ROOT_BASE;
        await axios.delete(`${rootBase}organizations/${this.currentOrganization.id}/users/${userId}`);
        
        return true;
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to remove user";
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    
    // Clear organization data (used during logout)
    clearOrganizationData() {
      this.organizations = [];
      this.currentOrganization = null;
      localStorage.removeItem(`${PREFIX}current_organization`);
    }
  }
});